import { useCallback, useEffect, useRef, useState } from "react";
import config from "@/config";
import type {
  StructuredOutputBackgroundRegressionJob,
  StructuredOutputBackgroundRegressionRequestBody,
} from "@/types/structured-output-background-regression";

const POLL_INTERVAL_MS = 1000;

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${config.API_BASE_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = "";
    try {
      const data = (await res.json()) as { detail?: unknown };
      if (typeof data.detail === "string") detail = data.detail;
    } catch {
      // ignore
    }
    throw new Error(detail || `${path} failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${config.API_BASE_URL}${path}`, { signal });
  if (!res.ok) {
    let detail = "";
    try {
      const data = (await res.json()) as { detail?: unknown };
      if (typeof data.detail === "string") detail = data.detail;
    } catch {
      // ignore
    }
    throw new Error(detail || `${path} failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

function isTerminal(status: StructuredOutputBackgroundRegressionJob["status"]): boolean {
  return status === "completed" || status === "failed";
}

export function useStructuredOutputBackgroundRegression() {
  const [job, setJob] = useState<StructuredOutputBackgroundRegressionJob | null>(
    null,
  );
  const [starting, setStarting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollAbortRef = useRef<AbortController | null>(null);

  const stopPolling = useCallback(() => {
    pollAbortRef.current?.abort();
    pollAbortRef.current = null;
    setPolling(false);
  }, []);

  const pollJob = useCallback(
    async (jobId: string, signal: AbortSignal) => {
      const data = await getJson<StructuredOutputBackgroundRegressionJob>(
        `/api/dashboard/structured-output-rerun/background-regression/${jobId}`,
        signal,
      );
      setJob(data);
      return data;
    },
    [],
  );

  const pollUntilDone = useCallback(
    async (jobId: string) => {
      stopPolling();
      const ac = new AbortController();
      pollAbortRef.current = ac;
      setPolling(true);

      try {
        let latest = await pollJob(jobId, ac.signal);
        while (!isTerminal(latest.status)) {
          await new Promise((resolve, reject) => {
            const timer = window.setTimeout(resolve, POLL_INTERVAL_MS);
            ac.signal.addEventListener(
              "abort",
              () => {
                window.clearTimeout(timer);
                reject(new DOMException("Polling aborted", "AbortError"));
              },
              { once: true },
            );
          });
          latest = await pollJob(jobId, ac.signal);
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") {
          return;
        }
        const msg = err instanceof Error ? err.message : "Polling failed";
        setError(msg);
        throw err;
      } finally {
        if (pollAbortRef.current === ac) {
          pollAbortRef.current = null;
          setPolling(false);
        }
      }
    },
    [pollJob, stopPolling],
  );

  const start = useCallback(
    async (body: StructuredOutputBackgroundRegressionRequestBody) => {
      setStarting(true);
      setError(null);
      try {
        const created = await postJson<StructuredOutputBackgroundRegressionJob>(
          "/api/dashboard/structured-output-rerun/background-regression",
          body,
        );
        setJob(created);
        void pollUntilDone(created.job_id);
        return created;
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : "Failed to start background regression";
        setError(msg);
        throw err;
      } finally {
        setStarting(false);
      }
    },
    [pollUntilDone],
  );

  const reset = useCallback(() => {
    stopPolling();
    setJob(null);
    setError(null);
  }, [stopPolling]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  return {
    job,
    starting,
    polling,
    error,
    start,
    reset,
    stopPolling,
  };
}
